import React, { useState, useEffect } from 'react';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import StockService from '../../Api_Module/Api_Services/StockService';

interface MetricSearchProps {
    availableMetrics: Metric[];
    selectedMetrics: Metric[];
    onMetricSelect: (metric: Metric) => void;
    onMetricRemove: (metricId: string) => void;
    symbol: string;
    containerClassName?: string; // optional wrapper class override
}

interface Category {
    displayName: string;
    name: string;
    description: string;
}

interface SubCategory {
    displayName: string;
    name: string;
    description: string;
    parentCategory: string;
}

const MetricSearch: React.FC<MetricSearchProps> = ({
    availableMetrics,
    selectedMetrics,
    onMetricSelect,
    onMetricRemove,
    symbol,
    containerClassName
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [showMetricSelector, setShowMetricSelector] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [metrics, setAvailableMetrics] = useState<Metric[]>(availableMetrics || []);
    const [isSubCategoryView, setIsSubCategoryView] = useState(false);
    const [allMetrics, setAllMetrics] = useState<Metric[]>([]);

    useEffect(() => {
        if (showMetricSelector && categories.length === 0) {
            getCategories();
        }
    }, [showMetricSelector, symbol]);

    useEffect(() => {
        const fetchAllMetrics = async () => {
            if (symbol) {
                try {
                    const result = await StockService.getMetricKeysByProvider();
                    if (result) {
                        setAllMetrics(result.map((m: any) => ({
                            metricAttributeName: m.metricAttributeName,
                            label: m.label,
                            tooltip: m.tooltip,
                            appendString: m.appendString,
                            appendFormatRepresent: m.appendFormatRepresent,
                            percentage: m.percentage,
                        })));
                    }
                } catch (error) {
                    console.error("Error fetching all metrics:", error);
                }
            }
        };
        fetchAllMetrics();
    }, [symbol]);

    const getMetrics = async (key: string, isSubCategory: boolean) => {
        try {
            let result;
            if (isSubCategory) {
                result = await StockService.getMetricsBySubCategory(symbol, key);
            } else {
                result = await StockService.getMetricsByCategory(symbol, key);
            }
            if (result) {
                setAvailableMetrics(result?.groups?.[0]?.metrics?.map((metric: any) => ({
                    metricAttributeName: metric.metricAttributeName,
                    label: metric.label,
                    category: key
                })) || []);
                console.log("Available Metrics:", result?.groups?.[0]?.metrics);
                console.log(metrics)
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const getCategories = async () => {
        try {
            const result = await StockService.getCategories(symbol);
            if (result) {
                setCategories(result.categories || []);
                setSubCategories(result.subCategories || []);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleSelectMetric = (metric: Metric) => {
        const isAlreadySelected = selectedMetrics.find(m => m.metricAttributeName === metric.metricAttributeName);

        if (isAlreadySelected) {
            onMetricRemove(metric.metricAttributeName ?? '');
        } else if (selectedMetrics.length < 5) {
            onMetricSelect(metric);
        }
    };

    const toggleCategoryExpansion = (categoryName: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryName)) {
            newExpanded.delete(categoryName);
        } else {
            newExpanded.add(categoryName);
        }
        setExpandedCategories(newExpanded);
    };

    const handleCategorySelect = (categoryName: string, isSubCategory: boolean = false, level: number = 1) => {
        console.log(`Category selected: ${categoryName}, Is SubCategory: ${isSubCategory}, Level: ${level}`);
        if (selectedCategory === categoryName) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(categoryName);
            if (level === 1 && !isSubCategory) {
                getMetrics(categoryName, false);
            }
            if (level === 2 && !isSubCategory) {
                getMetrics(categoryName, true);
            }

        }
    };

    const renderCategoryTree = () => {
        return categories.map(category => {
            const categorySubCategories = subCategories.filter(sub => sub.parentCategory === category.name);
            const isExpanded = expandedCategories.has(category.name);
            const hasSubCategories = categorySubCategories.length > 0;

            return (
                <div key={category.name}>
                    {/* Main Category */}
                    <div className="flex items-center">
                        {hasSubCategories && (
                            <button
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => toggleCategoryExpansion(category.name)}
                                className="p-1 hover:bg-theme-tertiary rounded mr-1"
                            >
                                {isExpanded ? (
                                    <ChevronDown className="w-3 h-3 text-theme-muted" />
                                ) : (
                                    <ChevronRight className="w-3 h-3 text-theme-muted" />
                                )}
                            </button>
                        )}
                        <button
                            onMouseDown={(e) => e.preventDefault()}
                            // onClick={() => handleCategorySelect(category.name, hasSubCategories, 1)}
                            onClick={() => (hasSubCategories ? toggleCategoryExpansion(category.name) : handleCategorySelect(category.name, hasSubCategories, 1))}
                            className={`flex-1 flex items-center gap-2 px-2 py-2 rounded text-left transition-colors text-sm ${selectedCategory === category.name
                                ? 'active-bg'
                                : ''
                                } ${!hasSubCategories ? 'ml-6' : ''}`}
                        >
                            <span className="font-medium">{category.displayName}</span>
                        </button>
                    </div>

                    {/* Sub Categories */}
                    {hasSubCategories && isExpanded && (
                        <div className="ml-6 mt-1 space-y-1">
                            {categorySubCategories.map(subCategory => (
                                <button
                                    key={subCategory.name}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleCategorySelect(subCategory.name, false, 2)}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors text-sm ${selectedCategory === subCategory.name
                                        ? 'active-bg'
                                        : ''
                                        }`}
                                >
                                    <span className="text-xs">•</span>
                                    <span>{subCategory.displayName}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className={`${containerClassName ?? 'mb-4'} relative`}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-muted w-5 h-5" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onFocus={() => setShowMetricSelector(true)}
                    onBlur={() => setTimeout(() => setShowMetricSelector(false), 200)}
                    placeholder={symbol ? "Search for 20,000+ metrics, segments, & KPIs" : "Select a symbol to search metrics"}
                    disabled={!symbol}
                    className={`w-full pl-10 pr-4 py-3 bg-theme-secondary border border-theme-tertiary rounded-lg text-theme-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!symbol ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
            </div>

            {showMetricSelector && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-theme-primary rounded-lg border border-theme-tertiary shadow-2xl z-50 w-full"
                    style={{
                        maxHeight: 'min(70vh, 560px)',
                        overflow: 'hidden'
                    }}>
                    <div className="flex">
                        <div className="w-80 bg-theme-secondary border-r border-theme-tertiary p-4">
                            <h3 className="text-theme-secondary text-xs font-semibold mb-3 uppercase tracking-wide">
                                CATEGORIES
                            </h3>
                            <div className="space-y-1 max-h-[48vh] overflow-y-auto pr-1" style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#4B5563 #1F2937'
                            }}>
                                {renderCategoryTree()}
                            </div>
                        </div>

                        <div
                            className="flex-1 p-4 max-h-[48vh] overflow-y-auto"
                            style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#4B5563 #1F2937'
                            }}
                        >
                            {searchTerm && (
                                <>
                                    <h4 className="text-theme-secondary text-xs font-semibold mb-3 uppercase tracking-wide">
                                        Search Results
                                    </h4>
                                    <div className="space-y-2">
                                        {allMetrics
                                            .filter(metric => {
                                                const matchesSearch = metric?.label?.toLowerCase().includes(searchTerm.toLowerCase()) || metric.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
                                                return matchesSearch;
                                            })
                                            .length > 0 ? (
                                            allMetrics
                                                .filter(metric => {
                                                    const matchesSearch = metric?.label?.toLowerCase().includes(searchTerm.toLowerCase()) || metric.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
                                                    return matchesSearch;
                                                })
                                                .map(metric => {
                                                    const isSelected = selectedMetrics.find(m => m.metricAttributeName === metric.metricAttributeName);

                                                    return (
                                                        <button
                                                            key={metric.metricAttributeName}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleSelectMetric(metric)}
                                                            className={`w-full flex items-center justify-between gap-2 p-2 rounded text-left text-sm transition-colors ${isSelected
                                                                ? 'bg-theme-tertiary/20 border border-theme-tertiary text-green-400'
                                                                : 'hover:bg-theme-tertiary border-theme-tertiary hover:border-theme-tertiary text-theme-primary'
                                                                }`}
                                                        >
                                                            <div className="flex-1">
                                                                <div className="font-medium">{metric.label}</div>
                                                                {/* <div className="text-theme-muted text-xs">{metric.category}</div> */}
                                                            </div>
                                                        </button>
                                                    );
                                                })
                                        ) : (
                                            <div className="text-theme-muted text-center py-4 text-sm">
                                                No metrics found matching "{searchTerm}"
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {!searchTerm && selectedCategory && (
                                <>
                                    <h4 className="text-theme-secondary text-xs font-semibold mb-3 uppercase tracking-wide">
                                        {categories.find(c => c.name === selectedCategory)?.displayName ||
                                            subCategories.find(sc => sc.name === selectedCategory)?.displayName || 'Metrics'}
                                    </h4>
                                    <div className="space-y-2">
                                        {metrics
                                            .filter(metric => metric.category === selectedCategory)
                                            .length > 0 ? (
                                            metrics
                                                .filter(metric => metric.category === selectedCategory)
                                                .map(metric => {
                                                    const isSelected = selectedMetrics.find(m => m.metricAttributeName === metric.metricAttributeName);

                                                    return (
                                                        <button
                                                            key={metric.metricAttributeName}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleSelectMetric(metric)}
                                                            className={`w-full flex items-center justify-between gap-2 p-2 rounded text-left text-sm transition-colors ${isSelected
                                                                ? 'active-border border'
                                                                : 'hover:bg-theme-tertiary border-theme-tertiary hover:border-theme-tertiary text-theme-primary'
                                                                }`}
                                                        >
                                                            <div className="flex-1">
                                                                <div className="font-medium">{metric.label}</div>
                                                                {/* <div className="text-theme-muted text-xs">{metric.category}</div> */}
                                                            </div>
                                                        </button>
                                                    );
                                                })
                                        ) : (
                                            <div className="text-theme-muted text-center py-4 text-sm">
                                                No metrics available in this category
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetricSearch;
